import { CheckCircleFilled, ClockCircleFilled, DownloadOutlined, ExclamationCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Empty, Tooltip } from 'antd';
import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { httpCreators } from '@ppvrbh/merchant-frontend-core';
import { RBHAlert, RBHBox, RBHMultiSelect, RBHText } from '../../../../../shared/components';
import { UtilHelper } from '../../../../../shared/helpers/utility';
import { GENERAL_STATE_KEY, generalStateSelector } from '../../../../../redux/general/general-reducer';
import RBHTable2 from '../../../../../shared/components/rbh-table-2';
import { Colors } from '../../../../../shared/theme';
import { CUSTOM_REPORT_ALERT_DURATION_MS, CUSTOM_REPORT_REQUEST_ID_LIST, CUSTOM_REPORT_STATUS_FILTER_OPTIONS, CUSTOM_REPORT_TYPE } from '../../redux/custom-report-constant';
import { customReportSagas } from '../../redux/custom-report-saga';
import { CustomReportService } from '../../redux/custom-report-service';
import styles from '../../styles';
import '../../styles.css';
import { textStyles } from '../../../../../shared/theme/fonts-ppv';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CustomReportItem {
    id: number;
    requestId: string;
    shopName: string | null;
    shopId: string | null;
    branchCode: string | null;
    reportType: string;
    startDate: string;
    endDate: string;
    status: string;
    filePath: string | null;
}

export interface ListMeta {
    totalItems: number;
    currentPage: number;
    itemsPerPage: number;
}

export interface RequestListProps {
    listData: CustomReportItem[];
    listMeta: ListMeta;
    listError: boolean;
    onListErrorClose: () => void;
    reportTypeFilter: string[];
    statusFilter: string[];
    onReportTypeChange: (value: string[]) => void;
    onStatusChange: (value: string[]) => void;
    onClearFilter: () => void;
    onPageChange: (pagination: any) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────


const getFilename = (filePath: string) => filePath.split('/').pop() || filePath;

const truncateFilename = (filePath: string, maxLength = 18) => {
    const filename = getFilename(filePath);
    if (filename.length <= maxLength) return filename;
    const dotIndex = filename.lastIndexOf('.');
    const ext = dotIndex !== -1 ? filename.slice(dotIndex) : '';
    const keep = maxLength - ext.length - 3;
    return `${filename.slice(0, Math.max(keep, 1))}...${ext}`;
};

const TOOLTIP_KEYS = [
    'main.custom-report.table.col.report-name.tooltip.stm',
    'main.custom-report.table.col.report-name.tooltip.tnv',
];

const ReportNameTooltipContent: React.FC = () => (
    <RBHBox style={styles.tooltipContent}>
        <RBHText
            style={styles.tooltipText}
            text="main.custom-report.table.col.report-name.tooltip.title"
        />
        {TOOLTIP_KEYS.map((key) => (
            <RBHText key={key} style={styles.tooltipText} text={key} />
        ))}
    </RBHBox>
);

// ── Status Pill ──────────────────────────────────────────────────────────────

const STATUS_PILL_CONFIG: Record<string, { bg: string; color: string; iconColor?: string; icon: React.ReactNode }> = {
    PENDING:    { bg: Colors.yellow100,    color: Colors.yellow800, iconColor: Colors.yellow600, icon: <ClockCircleFilled translate={null} /> },
    PROCESSING: { bg: Colors.yellow100,    color: Colors.yellow800, iconColor: Colors.yellow600, icon: <ClockCircleFilled translate={null} /> },
    COMPLETED:  { bg: Colors.tagPalegreen, color: Colors.green600,  icon: <CheckCircleFilled translate={null} /> },
    FAILED:     { bg: Colors.tagPalered,   color: Colors.red800,    icon: <ExclamationCircleFilled translate={null} /> },
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const config = STATUS_PILL_CONFIG[status];
    if (!config) return <span>{status || '-'}</span>;
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px',
            borderRadius: 24,
            backgroundColor: config.bg,
            color: config.color,
            whiteSpace: 'nowrap',
            ...textStyles.body3,
        }}>
            <span style={{ color: config.iconColor ?? config.color, lineHeight: 1 }}>{config.icon}</span>
            <FormattedMessage id={`main.custom-report.status.${status}`} />
        </span>
    );
};

// ── RequestList ──────────────────────────────────────────────────────────────

const RequestList: React.FC<RequestListProps> = ({
    listData,
    listMeta,
    listError,
    onListErrorClose,
    reportTypeFilter,
    statusFilter,
    onReportTypeChange,
    onStatusChange,
    onClearFilter,
    onPageChange,
}) => {
    const dispatch = useDispatch();
    const lang = useSelector(generalStateSelector(GENERAL_STATE_KEY.LOCALE));
    const [downloadError, setDownloadError] = useState(false);
    const [downloadSuccess, setDownloadSuccess] = useState(false);

    const reportTypeOptions = useMemo(
        () => Object.values(CUSTOM_REPORT_TYPE).map((type) => ({
            id: type,
            label: `main.custom-report.report-type.${type}`,
        })),
        []
    );

    const statusOptions = CUSTOM_REPORT_STATUS_FILTER_OPTIONS;

    const handleDownload = (requestId: string) => {
        dispatch(httpCreators.request({
            id: CUSTOM_REPORT_REQUEST_ID_LIST.DOWNLOAD_CUSTOM_REPORT,
            sagaHandler: customReportSagas.downloadCustomReportHandler,
            service: CustomReportService.downloadCustomReport,
            serviceParams: { requestId },
            callbackParams: {
                onSuccess: (downloadUrl: string) => { window.open(downloadUrl, '_blank', 'noopener,noreferrer'); setDownloadSuccess(true); },
                onFailure: () => { setDownloadError(true); },
            },
        }));
    };

    const columns = useMemo(() => [
        {
            width: 200,
            title: <FormattedMessage id="main.custom-report.table.col.date-range" />,
            dataIndex: 'startDate',
            render: (_: any, record: CustomReportItem) => (
                <span style={textStyles.subtitle2}>{UtilHelper.formatShortDate(record.startDate, lang)} - {UtilHelper.formatShortDate(record.endDate, lang)}</span>
            ),
        },
        {
            width: 156,
            title: <FormattedMessage id="main.custom-report.table.col.tax-branch" />,
            dataIndex: 'branchCode',
            render: (value: string | null) => <span style={textStyles.subtitle2}>{value || '-'}</span>,
        },
        {
            width: 240,
            title: <FormattedMessage id="main.custom-report.table.col.branch" />,
            dataIndex: 'shopName',
            render: (value: string | null, record: CustomReportItem) => (
                <div>
                    <div style={{ ...textStyles.subtitle2}}>{value || <FormattedMessage id="main.custom-report.table.all-branches" />}</div>
                    {record.shopId && <div style={{ ...textStyles.body3, color: Colors.grey600 }}>{record.shopId}</div>}
                </div>
            ),
        },
        {
            width: 140,
            title: <FormattedMessage id="main.custom-report.table.col.report-type" />,
            dataIndex: 'reportType',
            render: (value: string) => (
                <span style={textStyles.subtitle2}>
                    {(Object.values(CUSTOM_REPORT_TYPE) as string[]).includes(value)
                        ? <FormattedMessage id={`main.custom-report.report-type.${value}`} />
                        : (value || '-')}
                </span>
            ),
        },
        {
            width: 180,
            title: (
                <RBHBox style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FormattedMessage id="main.custom-report.table.col.report-name" />
                    <Tooltip
                        title={<ReportNameTooltipContent />}
                        overlayStyle={{ backgroundColor: Colors.grey800, borderRadius: '4px', padding: '4px 8px' }}
                        overlayClassName="custom-report-name-tooltip"
                    >
                        <InfoCircleOutlined translate={null} />
                    </Tooltip>
                </RBHBox>
            ),
            dataIndex: 'filePath',
            render: (value: string | null) => value ? (
                <Tooltip title={getFilename(value)}>
                    <span style={textStyles.subtitle2}>{truncateFilename(value)}</span>
                </Tooltip>
            ) : <span style={textStyles.subtitle2}>-</span>,
        },
        {
            width: 124,
            title: <FormattedMessage id="main.custom-report.table.col.status" />,
            dataIndex: 'status',
            render: (value: string) => value ? <StatusPill status={value} /> : '-',
        },
        {
            width: 64,
            title: '',
            dataIndex: 'filePath',
            render: (_: string | null, record: CustomReportItem) => record.filePath ? (
                <DownloadOutlined
                    translate={null}
                    style={{ fontSize: 20, color: Colors.violet, cursor: 'pointer' }}
                    onClick={() => handleDownload(record.requestId)}
                />
            ) : null,
        },
    ], [handleDownload, lang]);

    const emptyState = (
        <RBHBox style={styles.emptyContainer}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={null} />
            <RBHText
                data-testid="custom-report-empty-title"
                id="custom-report-empty-title"
                style={styles.emptyTitle}
                text="main.custom-report.empty.title"
            />
            <RBHText
                data-testid="custom-report-empty-subtitle"
                id="custom-report-empty-subtitle"
                style={styles.emptySubtitle}
                text="main.custom-report.empty.subtitle"
            />
        </RBHBox>
    );

    return (
        <RBHBox style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {downloadSuccess && (
                <div className="custom-report-alert-container" style={styles.alertContainer}>
                    <RBHAlert
                        type="success"
                        text="main.custom-report.alert.download-success"
                        autoCloseDuration={CUSTOM_REPORT_ALERT_DURATION_MS}
                        afterClose={() => setDownloadSuccess(false)}
                    />
                </div>
            )}
            {downloadError && (
                <div className="custom-report-alert-container" style={styles.alertContainer}>
                    <RBHAlert
                        type="error"
                        text="main.custom-report.alert.download-error"
                        autoCloseDuration={CUSTOM_REPORT_ALERT_DURATION_MS}
                        afterClose={() => setDownloadError(false)}
                    />
                </div>
            )}
            {listError && (
                <div className="custom-report-alert-container" style={styles.alertContainer}>
                    <RBHAlert
                        type="error"
                        text="main.custom-report.alert.error"
                        autoCloseDuration={CUSTOM_REPORT_ALERT_DURATION_MS}
                        afterClose={onListErrorClose}
                    />
                </div>
            )}
            <RBHBox style={styles.filterRow} className="custom-report-filter-row">
                <RBHBox style={styles.listTitleGroup}>
                    <RBHText
                        data-testid="custom-report-list-title"
                        id="custom-report-list-title"
                        style={styles.listTitleText}
                        text="main.custom-report.list-title"
                    />
                    <span
                        data-testid="custom-report-count-badge"
                        id="custom-report-count-badge"
                        style={styles.countBadge}
                    >
                        {listMeta.totalItems}
                    </span>
                </RBHBox>
                <RBHBox style={styles.filterChips} className="custom-report-filter-chips">
                    <RBHMultiSelect
                        id="custom-report-filter-report-type"
                        label="main.custom-report.filter.report-type"
                        options={reportTypeOptions}
                        selectedIds={reportTypeFilter}
                        mode='radio'
                        onChange={onReportTypeChange}
                        style={styles.filterSelect}
                        hideSearch
                        hideSelectAll
                    />
                    <RBHMultiSelect
                        id="custom-report-filter-status"
                        label="main.custom-report.filter.status"
                        options={statusOptions}
                        selectedIds={statusFilter}
                        onChange={onStatusChange}
                        style={styles.filterSelect}
                        hideSearch
                        hideSelectAll
                    />
                    {(reportTypeFilter.length > 0 || statusFilter.length > 0) && (
                        <span
                            data-testid="custom-report-clear-filter"
                            onClick={onClearFilter}
                            style={styles.clearFilterText}
                        >
                            Clear Filter
                        </span>
                    )}
                </RBHBox>
            </RBHBox>
            <div className="custom-report-table-wrapper">
                <RBHTable2
                    rowKey="requestId"
                    columns={columns}
                    dataSource={listData}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: emptyState }}
                    pagination={{
                        pageSize: listMeta.itemsPerPage,
                        current: listMeta.currentPage,
                        total: listMeta.totalItems,
                        showSizeChanger: true,
                    }}
                    onChange={onPageChange}
                />
            </div>
        </RBHBox>
    );
};

export default RequestList;
